import { parseISO } from 'date-fns'
import { createContext, useContext, useEffect, useState } from 'react'

import { UserContextStore } from '@/context/user/user'
import { useLocalStorage } from '@/hooks/useLocalStorage'
import { Type } from '@/types/Ticket'

export type SortBy = {
  desc: boolean
  id: string
}

export interface FiltersState {
  endingCreatedAtDate?: Date
  priority: null | string
  searchString: string | null
  sortBy: null | SortBy
  startingCreatedAtDate?: Date
  ticketAssigneeId: null | number
  ticketReasons: null | string[]
  ticketStatuses: null | string[]
  ticketTypes: null | string[]
}

export const initialFilterState: FiltersState = {
  endingCreatedAtDate: undefined,
  priority: null,
  ticketAssigneeId: null,
  ticketReasons: null,
  ticketStatuses: null,
  ticketTypes: null,
  searchString: null,
  sortBy: null,
  startingCreatedAtDate: undefined,
}

export const currentTicketFiltersKey = 'active-ticket-filters'

interface FiltersContextType extends FiltersState {
  setFilters: (filters: Partial<FiltersState>) => void
  setSearchString: (val: string) => void
  setSortBy: (val: SortBy | null) => void
  storedFiltersLoaded: boolean
}

export const FiltersContextStore = createContext<FiltersContextType | undefined>(undefined)

const TicketFiltersContextProvider = ({
  children,
  value = initialFilterState,
}: {
  children: React.ReactNode
  value?: FiltersState
}) => {
  const [storedFilters, setStoredFilters] = useLocalStorage(currentTicketFiltersKey, initialFilterState)
  const [storedFiltersLoaded, setStoredFiltersLoaded] = useState(false)
  const [filtersState, setFiltersState] = useState(value)
  // Read the user context directly (rather than the throwing useUserContext hook)
  // so this provider works in tests that don't mount a UserContextProvider.
  const userContext = useContext(UserContextStore)
  const isSuperAdmin = !!userContext?.user?.isSuperAdmin
  const userLoaded = !!userContext?.user
  const setFilters = (filters: Partial<FiltersState>) => {
    setFiltersState((prevState) => {
      return {
        ...prevState,
        ...filters,
      }
    })
  }

  const setSearchString = (val: string) => {
    setFiltersState((prevState) => {
      return {
        ...prevState,
        searchString: val.length > 0 ? val : null,
      }
    })
  }

  const setSortBy = (val: SortBy | null) => {
    setFiltersState((prevState) => {
      return {
        ...prevState,
        sortBy: val ? val : null,
      }
    })
  }

  useEffect(() => {
    if (!storedFiltersLoaded) {
      if (storedFilters.startingCreatedAtDate) {
        storedFilters.startingCreatedAtDate = parseISO(storedFilters.startingCreatedAtDate.toString())
      }

      if (storedFilters.endingCreatedAtDate) {
        storedFilters.endingCreatedAtDate = parseISO(storedFilters.endingCreatedAtDate.toString())
      }

      setFiltersState(storedFilters)
      setStoredFiltersLoaded(true)
    }
  }, [storedFiltersLoaded, storedFilters])

  // Non-admins must never carry the Conversation type in their persisted filters.
  // Gate on userLoaded so a super-admin's saved filter isn't stripped while the
  // user context is still hydrating from localStorage (user starts null).
  useEffect(() => {
    if (!storedFiltersLoaded || !userLoaded || isSuperAdmin) return
    setFiltersState((prevState) => {
      if (!prevState.ticketTypes?.includes(Type.CONVERSATION)) return prevState
      return { ...prevState, ticketTypes: prevState.ticketTypes.filter((t) => t !== Type.CONVERSATION) }
    })
  }, [storedFiltersLoaded, userLoaded, isSuperAdmin])

  useEffect(() => {
    setStoredFilters(filtersState)
  }, [filtersState, setStoredFilters])

  return (
    <FiltersContextStore.Provider
      value={{
        ...filtersState,
        setSearchString,
        setFilters,
        setSortBy,
        storedFiltersLoaded,
      }}
    >
      {children}
    </FiltersContextStore.Provider>
  )
}

export const useTicketFiltersContext = () => {
  const context = useContext(FiltersContextStore)
  if (context === undefined) {
    throw new Error('useFiltersContext must be used within a FiltersContextProvider')
  }
  return context
}

export const getPreviousSavedTicketFilters = (parsedState?: FiltersState) => {
  if (parsedState) {
    return parsedState
  } else {
    return initialFilterState
  }
}

export default TicketFiltersContextProvider
